import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Plus, ExternalLink } from 'lucide-react';
import type { Event } from '@peles/shared';
import { useUpdateEvent } from '../../../hooks/useEvents';
import { Popover } from '../../common/Popover';
import { VenueSelect } from '../../common/VenueSelect';
import { EventWhenFields } from '../create/EventWhenFields';
import { QuickVenueForm } from '../create/QuickVenueForm';
import { buildEventWhen, whenFromEvent, type EventWhen } from '../create/eventWhen';
import { formatEventDateTime } from '../../../utils/eventDates';
import { extractApiErrorMessage } from '../../../utils/apiError';

const chip =
  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gray-200 text-xs text-gray-600 bg-white hover:border-primary-300 hover:text-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300';

type EditableEvent = Pick<Event, 'id' | 'startAt' | 'endAt' | 'venueId' | 'venueName'>;

function DateForm({ event, close }: { event: EditableEvent; close: () => void }) {
  const update = useUpdateEvent();
  const [when, setWhen] = useState<EventWhen>(() => whenFromEvent(event.startAt, event.endAt));
  const [error, setError] = useState('');

  const save = async () => {
    const result = buildEventWhen(when);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    try {
      await update.mutateAsync({
        id: event.id,
        updates: { startAt: result.startAt, endAt: result.endAt },
      });
      close();
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Could not change the date'));
    }
  };

  return (
    <div className="space-y-4">
      {error && <div className="px-3 py-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>}
      <EventWhenFields value={when} onChange={setWhen} autoFocus />
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={close}
          className="h-9 px-4 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={update.isPending}
          className="h-9 px-4 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

/** The header's date pill: click it to change the date and times in place. */
export function EventDateChip({ event }: { event: EditableEvent }) {
  return (
    <Popover
      ariaLabel="Change date"
      panelClassName="w-[22rem]"
      trigger={(props) => (
        <button type="button" {...props} className={chip} title="Change date">
          <Calendar size={13} className="text-gray-400" />
          {event.startAt ? formatEventDateTime(event.startAt, event.endAt) : 'Add date'}
        </button>
      )}
    >
      {(close) => <DateForm event={event} close={close} />}
    </Popover>
  );
}

function VenueForm({ event, close }: { event: EditableEvent; close: () => void }) {
  const update = useUpdateEvent();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const choose = async (venueId: string | null) => {
    if (!venueId || venueId === event.venueId) return;
    setError('');
    try {
      await update.mutateAsync({ id: event.id, updates: { venueId } });
      close();
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Could not change the venue'));
    }
  };

  if (adding) {
    return (
      <QuickVenueForm
        onCancel={() => setAdding(false)}
        onCreated={(venue) => {
          // Back to the picker, so a failed assignment shows its error there
          // and retrying picks the venue just made rather than making another.
          setAdding(false);
          void choose(venue.id);
        }}
      />
    );
  }

  return (
    <div className="space-y-3">
      {error && <div className="px-3 py-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>}
      <VenueSelect
        value={event.venueId}
        onChange={(id) => void choose(id)}
        disabled={update.isPending}
        placeholder="Search venues"
        ariaLabel="Venue"
      />
      <div className="flex items-center justify-between gap-2 text-sm">
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1.5 font-medium text-primary-700 hover:text-primary-800"
        >
          <Plus size={15} />
          New venue
        </button>
        {event.venueId && (
          <Link
            to={`/venues/${event.venueId}`}
            className="inline-flex items-center gap-1 text-gray-500 hover:text-primary-700"
          >
            Open venue
            <ExternalLink size={13} />
          </Link>
        )}
      </div>
    </div>
  );
}

/** The header's venue pill: click it to pick another venue, or add one. */
export function EventVenueChip({ event }: { event: EditableEvent }) {
  return (
    <Popover
      ariaLabel="Change venue"
      trigger={(props) => (
        <button type="button" {...props} className={chip} title="Change venue">
          <MapPin size={13} className="text-gray-400" />
          {event.venueName || 'Add venue'}
        </button>
      )}
    >
      {(close) => <VenueForm event={event} close={close} />}
    </Popover>
  );
}
